import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Metadata } from '@memberjunction/core';
import { BrandConfig } from '../../types';

interface BrandField {
  key: string;
  label: string;
  placeholder: string;
  rows: number;
}

@Component({
  standalone: false,
  selector: 'mkt-brand-config',
  template: `
    <div class="mkt-brand-overlay" (click)="onClose()">
      <div class="mkt-brand-drawer" (click)="$event.stopPropagation()">
        <div class="mkt-brand-drawer__header">
          <h3><i class="fa-solid fa-palette"></i> Brand Configuration</h3>
          <button class="mkt-brand-drawer__close" (click)="onClose()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Loading state -->
        <div class="mkt-brand-drawer__body" *ngIf="loading">
          <div class="mkt-brand-drawer__loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <p>Loading brand configuration...</p>
          </div>
        </div>

        <!-- Form -->
        <div class="mkt-brand-drawer__body" *ngIf="!loading">
          <!-- Company selector -->
          <div class="mkt-brand-field">
            <label class="mkt-brand-field__label">Company</label>
            <select
              class="mkt-brand-field__input"
              [ngModel]="selectedCompanyId"
              (ngModelChange)="onCompanyChange($event)"
              [disabled]="saving">
              <option *ngFor="let c of companies" [value]="c.ID">{{ c.Name }}</option>
            </select>
          </div>

          <!-- Brand Name (always visible) -->
          <div class="mkt-brand-field">
            <label class="mkt-brand-field__label">Brand Name <span class="mkt-brand-field__req">*</span></label>
            <input
              *ngIf="editing"
              class="mkt-brand-field__input"
              [ngModel]="formData['BrandName']"
              (ngModelChange)="formData['BrandName'] = $event"
              placeholder="e.g. Sidecar"
              [disabled]="saving" />
            <p class="mkt-brand-field__value mkt-brand-field__value--heading" *ngIf="!editing">
              {{ activeConfig?.BrandName || 'Not configured' }}
            </p>
          </div>

          <!-- Textarea fields -->
          <ng-container *ngFor="let field of fields">
            <div class="mkt-brand-field">
              <label class="mkt-brand-field__label">{{ field.label }}</label>
              <textarea
                *ngIf="editing"
                class="mkt-brand-field__textarea"
                [(ngModel)]="formData[field.key]"
                [placeholder]="field.placeholder"
                [rows]="field.rows"
                [disabled]="saving">
              </textarea>
              <p class="mkt-brand-field__value" *ngIf="!editing">
                {{ getConfigField(field.key) || '—' }}
              </p>
            </div>
          </ng-container>

          <!-- Error message -->
          <div class="mkt-brand-drawer__error" *ngIf="errorMessage">
            <i class="fa-solid fa-circle-exclamation"></i> {{ errorMessage }}
          </div>

          <!-- Action bar -->
          <div class="mkt-brand-drawer__actions">
            <ng-container *ngIf="!editing">
              <button class="mkt-brand-btn mkt-brand-btn--primary" (click)="startEditing()">
                <i class="fa-solid fa-pen"></i> {{ activeConfig ? 'Edit Configuration' : 'Set Up Brand' }}
              </button>
            </ng-container>
            <ng-container *ngIf="editing">
              <button class="mkt-brand-btn mkt-brand-btn--secondary" (click)="cancelEditing()" [disabled]="saving">
                Cancel
              </button>
              <button class="mkt-brand-btn mkt-brand-btn--primary" (click)="save()" [disabled]="saving || !formData['BrandName']?.trim()">
                <i class="fa-solid" [ngClass]="saving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                {{ saving ? 'Saving...' : 'Save Configuration' }}
              </button>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .mkt-brand-overlay {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: var(--mj-bg-overlay);
      z-index: 1000;
      display: flex;
      justify-content: flex-end;
      animation: mktFadeIn 0.15s ease;
    }
    .mkt-brand-drawer {
      width: 520px;
      max-width: 90vw;
      background: var(--mj-bg-surface);
      height: 100%;
      display: flex;
      flex-direction: column;
      animation: mktDrawerIn 0.25s ease;
    }
    .mkt-brand-drawer__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 20px;
      border-bottom: 1px solid var(--mj-border-default);
      flex-shrink: 0;
    }
    .mkt-brand-drawer__header h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 700;
      color: var(--mj-text-primary);
    }
    .mkt-brand-drawer__header h3 i {
      color: var(--mj-brand-primary);
      margin-right: 6px;
    }
    .mkt-brand-drawer__close {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-secondary);
      font-size: 1rem;
      padding: 4px 8px;
    }
    .mkt-brand-drawer__close:hover { color: var(--mj-text-primary); }

    .mkt-brand-drawer__body {
      flex: 1;
      overflow-y: auto;
      padding: 20px;
    }

    .mkt-brand-drawer__loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      color: var(--mj-text-muted);
    }
    .mkt-brand-drawer__loading i {
      font-size: 1.5rem;
      margin-bottom: 12px;
    }

    /* Fields */
    .mkt-brand-field {
      margin-bottom: 18px;
    }
    .mkt-brand-field__label {
      display: block;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--mj-text-secondary);
      margin-bottom: 6px;
    }
    .mkt-brand-field__req {
      color: var(--mj-status-error);
    }
    .mkt-brand-field__input,
    .mkt-brand-field__textarea {
      width: 100%;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      padding: 10px 12px;
      font-size: 0.88rem;
      font-family: inherit;
      line-height: 1.5;
      box-sizing: border-box;
      transition: border-color 0.15s;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
    }
    .mkt-brand-field__input:focus,
    .mkt-brand-field__textarea:focus {
      outline: none;
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 12%, transparent);
    }
    .mkt-brand-field__input:disabled,
    .mkt-brand-field__textarea:disabled {
      background: var(--mj-bg-page);
      color: var(--mj-text-muted);
    }
    .mkt-brand-field__textarea {
      resize: vertical;
      min-height: 60px;
    }
    .mkt-brand-field__value {
      margin: 0;
      font-size: 0.88rem;
      line-height: 1.6;
      color: var(--mj-text-secondary);
      white-space: pre-wrap;
    }
    .mkt-brand-field__value--heading {
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--mj-text-primary);
    }

    /* Error */
    .mkt-brand-drawer__error {
      background: var(--mj-status-error-bg);
      border: 1px solid var(--mj-status-error-border);
      border-radius: 6px;
      padding: 10px 14px;
      color: var(--mj-status-error-text);
      font-size: 0.85rem;
      margin-bottom: 16px;
    }

    /* Actions */
    .mkt-brand-drawer__actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      padding-top: 16px;
      border-top: 1px solid var(--mj-border-default);
      margin-top: 8px;
    }
    .mkt-brand-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      border-radius: 6px;
      font-size: 0.88rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }
    .mkt-brand-btn--primary {
      background: var(--mj-brand-primary);
      color: var(--mj-text-inverse);
      border: 1px solid var(--mj-brand-primary);
    }
    .mkt-brand-btn--primary:hover:not(:disabled) {
      background: var(--mj-brand-primary-hover);
    }
    .mkt-brand-btn--primary:disabled {
      background: var(--mj-text-muted);
      border-color: var(--mj-text-muted);
      cursor: not-allowed;
    }
    .mkt-brand-btn--secondary {
      background: var(--mj-bg-surface);
      color: var(--mj-text-secondary);
      border: 1px solid var(--mj-border-default);
    }
    .mkt-brand-btn--secondary:hover:not(:disabled) {
      background: var(--mj-bg-surface-hover);
      border-color: var(--mj-brand-primary);
    }
    .mkt-brand-btn--secondary:disabled {
      color: var(--mj-text-muted);
      cursor: not-allowed;
    }

    @keyframes mktFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes mktDrawerIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }
  `]
})
export class BrandConfigComponent implements OnInit {
  @Input() companies: any[] = [];
  @Input() allConfigs: BrandConfig[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  selectedCompanyId: string = '';
  activeConfig: BrandConfig | null = null;

  editing = false;
  saving = false;
  loading = false;
  errorMessage = '';
  formData: Record<string, string> = {};

  fields: BrandField[] = [
    { key: 'BrandVoiceAndTone', label: 'Voice & Tone', placeholder: 'Describe how the brand sounds — e.g. "Professional yet approachable, confident but not arrogant, always educational"', rows: 3 },
    { key: 'TargetAudience', label: 'Target Audience', placeholder: 'Who are you writing for? e.g. "B2B SaaS decision-makers, mid-market (50-500 employees), technical but not developers"', rows: 3 },
    { key: 'ProductPositioning', label: 'Product Positioning', placeholder: 'How should the product be positioned? e.g. "The AI-powered platform that makes association management effortless"', rows: 3 },
    { key: 'KeyMessagingPillars', label: 'Key Messaging Pillars', placeholder: 'Core themes that all content should reinforce — one per line', rows: 3 },
    { key: 'BrandValues', label: 'Brand Values', placeholder: 'Core values that guide tone and messaging — e.g. "Innovation, Transparency, Member-first"', rows: 2 },
    { key: 'BrandPersonalityTraits', label: 'Personality Traits', placeholder: 'Traits that characterize the brand — e.g. "Helpful mentor, not a pushy salesperson"', rows: 2 },
    { key: 'BrandPriorities', label: 'Strategic Priorities', placeholder: 'Current priorities that should inform content topics and emphasis', rows: 2 },
    { key: 'BrandDosAndDonts', label: "Dos & Don'ts", placeholder: "Explicit guardrails — e.g. \"DO: Use data to back claims. DON'T: Bash competitors by name.\"", rows: 3 },
    { key: 'VisualIdentityGuidelines', label: 'Visual Identity Guidelines', placeholder: 'Colors, fonts, image style preferences for image generation validation', rows: 2 },
    { key: 'DefaultBlogAuthor', label: 'Default Blog Author', placeholder: 'Author name stamped on generated blog posts — e.g. "Sidecar team"', rows: 1 },
  ];

  ngOnInit(): void {
    // Default to the first company that has a config, or the first company
    const companyWithConfig = this.companies.find(c =>
      this.allConfigs.some(cfg => cfg.CompanyID === c.ID)
    );
    const defaultCompany = companyWithConfig || this.companies[0];
    if (defaultCompany) {
      this.selectedCompanyId = defaultCompany.ID;
      this.selectConfigForCompany(this.selectedCompanyId);
    }
    if (!this.activeConfig) {
      this.editing = true;
      this.initFormData();
    }
  }

  onCompanyChange(companyId: string): void {
    this.selectedCompanyId = companyId;
    this.selectConfigForCompany(companyId);
    this.errorMessage = '';
    if (!this.activeConfig) {
      this.editing = true;
      this.initFormData();
    } else {
      this.editing = false;
    }
  }

  private selectConfigForCompany(companyId: string): void {
    this.activeConfig = this.allConfigs.find(c => c.CompanyID === companyId) || null;
  }

  onClose(): void {
    this.close.emit();
  }

  startEditing(): void {
    this.initFormData();
    this.editing = true;
    this.errorMessage = '';
  }

  cancelEditing(): void {
    if (!this.activeConfig) {
      this.close.emit();
      return;
    }
    this.editing = false;
    this.errorMessage = '';
  }

  async save(): Promise<void> {
    if (!this.formData['BrandName']?.trim()) return;
    this.saving = true;
    this.errorMessage = '';

    try {
      let entity: BrandConfig;
      if (this.activeConfig) {
        entity = this.activeConfig;
      } else {
        const md = new Metadata();
        entity = await md.GetEntityObject<BrandConfig>('Brand Configurations');
        entity.NewRecord();
        entity.CompanyID = this.selectedCompanyId;
        entity.IsActive = true;
      }

      entity.BrandName = this.formData['BrandName'].trim();
      for (const field of this.fields) {
        const val = this.formData[field.key]?.trim() || null;
        (entity as any)[field.key] = val;
      }

      const success = await entity.Save();
      if (success) {
        this.editing = false;
        this.saved.emit();
      } else {
        this.errorMessage = 'Failed to save brand configuration. Please try again.';
      }
    } catch (err: any) {
      this.errorMessage = err?.message || 'An unexpected error occurred.';
    } finally {
      this.saving = false;
    }
  }

  getConfigField(key: string): string {
    if (!this.activeConfig) return '';
    return (this.activeConfig as any)[key] || '';
  }

  private initFormData(): void {
    this.formData = {
      BrandName: this.activeConfig?.BrandName || '',
    };
    for (const field of this.fields) {
      this.formData[field.key] = (this.activeConfig as any)?.[field.key] || '';
    }
  }
}
